import { Tooltip } from "@ubnt/store.core/lib/components/Tooltip";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useWindowSize } from "react-use";
import { CONTAINER_WIDTH } from "layout";
import tooltipIcon from "constants/modelCompareData/assets/tooltip-info.svg";
import { emptyColumn } from "constants/modelCompareData/data/products/emptyColumn";
import { useModelCompareTranslation } from "constants/modelCompareData/translations/useModelCompareTranslation";
import type { MutatedProduct } from "constants/modelCompareData/types";
import { useMenu } from "@ubnt/store.core/lib/contexts/MenuContext";
import { LanguageDirectionType } from "@ubnt/store.core/lib/contexts/MenuContext/utils";
import { StyleableSvgImage } from "@ubnt/store.core/lib/components/StyleableSvgImage";
import * as S from "../CompareTable.styles";
import { TOOLTIP_CLASS_NAME } from "../CompareTable.styles";
import { getNumberOfColumns } from "../helpers/getNumberOfColumns";
import { useButtonRenderer } from "../helpers/RenderButton";
import type { CompareTableMarkupProps } from "./CompareTableMarkup.types";
import { ModelCompareOverflowScrollButton } from "./components/CompareTableOverflowScrollButton";
import { EmptyColumnHeader } from "./components/EmptyColumnHeader/EmptyColumnHeader";
import { FootNotes } from "./components/FootNotes/FootNotes";
import { HeaderShadow } from "./components/HeaderShadow";
import { Notes } from "./components/Notes/Notes";
import { ProductHeader } from "./components/ProductHeader/ProductHeader";
import { PropertyValueGridItem } from "./components/PropertyValueGridItem";
import {
  StickyGrid,
  StickyGridBody,
  StickyGridHeader,
  StickyGridOverlay,
} from "./components/StickyGrid";

const SMOOTH_SIZE_REDUCTION = 4;

export const CompareTableMarkup = ({
  products,
  properties,
  columnWidth = 338,
  propertyColumnWidth = 230,
  headerStickOffset,
  overlayTopOffset,
  showBuyButton,
  onLinkClick,
  linkToStoreTitleClick,
  showBadges,
  setSelectedProducts,
  hasBeenObserved,
  selectedProductsLimit,
  setExternalState,
  selectedProducts,
  currAvailableProducts,
  isOverviewPage,
  isStaticTable,
}: CompareTableMarkupProps) => {
  const { storeId } = useMenu();
  const { formatMessage } = useModelCompareTranslation();
  const renderButton = useButtonRenderer();
  const { width: screenWidth } = useWindowSize();
  const isLessThanOrEqualToFiveProducts = currAvailableProducts.length <= 5;
  const [headerItemsAreSticking, setHeaderItemsAreSticking] = useState(false);

  // #############################################################################
  // Grid calculations
  // #############################################################################
  const isSmallScreen = useMemo(
    () => screenWidth < S.MOBILE_BREAKPOINT,
    [screenWidth],
  );
  const isGreaterThanDesktopWidth = useMemo(
    () => screenWidth > CONTAINER_WIDTH.DESKTOP,
    [screenWidth],
  );

  const leftColumnWidth = useMemo(
    () =>
      isSmallScreen
        ? 0
        : Math.min(screenWidth / SMOOTH_SIZE_REDUCTION, propertyColumnWidth),
    [isSmallScreen, propertyColumnWidth, screenWidth],
  );

  const maxImageHeight = useMemo(
    () => Math.max(...products.map(({ image }) => image.height)),
    [products],
  );

  const stickOffset = useMemo(
    () => (headerStickOffset ? headerStickOffset({ maxImageHeight }) : 0),
    [headerStickOffset, maxImageHeight],
  );

  const numberOfColumns = useMemo(
    () =>
      isStaticTable
        ? products.length
        : getNumberOfColumns({
            selectedProducts,
            selectedProductsLimit,
            products,
          }),
    [selectedProducts, selectedProductsLimit, products, isStaticTable],
  );

  const templateColumnWidth = useMemo(() => {
    if (isSmallScreen) return "100%";
    if (isGreaterThanDesktopWidth) return "1fr";
    return `${columnWidth}px`;
  }, [isSmallScreen, columnWidth, isGreaterThanDesktopWidth]);

  // const twoColumns = 2;
  /**
   * 1 sticky left column
   * N non-sticky product columns
   */
  const templateColumns = useMemo(
    () =>
      `${leftColumnWidth}px repeat(${numberOfColumns}, ${templateColumnWidth})`,
    [leftColumnWidth, numberOfColumns, templateColumnWidth],
  );

  const hasNotes = useMemo(() => {
    return products.some(({ notes }) => !!notes?.length);
  }, [products]);

  const hasFootnotes = useMemo(() => {
    return products.some(({ footnotes }) => footnotes?.length);
  }, [products]);

  // #############################################################################
  // Column button
  // #############################################################################
  const getComingSoonButtonText = useCallback(
    (product: MutatedProduct) => {
      const buttonText = product.comingSoonButtonText;

      if (typeof buttonText === "string") {
        return formatMessage(buttonText);
      }

      if (typeof buttonText === "function" && !!buttonText(storeId)) {
        const foundKey = buttonText(storeId);

        if (foundKey) {
          return formatMessage(foundKey);
        }
      }

      return formatMessage("Coming Soon");
    },
    [formatMessage, storeId],
  );

  const hasMultipleProductsSelected = useMemo(
    () => products.filter(({ id }) => id !== emptyColumn.id).length > 1,
    [products],
  );

  return (
    // APPLY left to right override in all cases rather than redo the model compare for Arabic
    <section dir={LanguageDirectionType.ltr}>
      <StickyGrid templateColumns={templateColumns}>
        <StickyGridHeader
          stickOffset={stickOffset}
          onStickyStateChange={setHeaderItemsAreSticking}
          nonScrollChildren={({ grid: { isAtMinX }, isStuck }) => {
            return (
              <HeaderShadow
                leftColumnWidth={leftColumnWidth}
                shadowLeftColumn={isAtMinX}
                show={isStuck}
              />
            );
          }}
        >
          <S.PlaceholderGridItem stickX />
          {products.map((product, index) =>
            product.propertyValues.length ? (
              <ProductHeader
                key={`${product.id}-${index}`}
                isStaticProduct={
                  isStaticTable || (!isOverviewPage && index === 0)
                }
                product={product}
                setSelectedProducts={setSelectedProducts}
                index={index}
                setExternalState={setExternalState}
                selectedProducts={selectedProducts}
                linkToStoreTitleClick={linkToStoreTitleClick}
                hasBeenObserved={hasBeenObserved}
                isLessThanOrEqualToFiveProducts={
                  isLessThanOrEqualToFiveProducts
                }
                hasMultipleProductsSelected={hasMultipleProductsSelected}
                isSmallScreen={isSmallScreen}
                onLinkClick={onLinkClick}
                showBadges={showBadges}
              />
            ) : (
              <EmptyColumnHeader
                key={`${product.id}-${index}`}
                product={product}
                index={index}
                hasBeenObserved={hasBeenObserved}
                maxImageHeight={maxImageHeight}
                headerItemsAreSticking={headerItemsAreSticking}
              />
            ),
          )}
        </StickyGridHeader>

        <StickyGridBody>
          {properties.map((property) => (
            <Fragment key={property.id}>
              <S.PropertyTitleGridItem stickX>
                {!!leftColumnWidth && (
                  <S.PropertyTitleGridItemContent>
                    <S.PropertyIcon>
                      <StyleableSvgImage
                        width={30}
                        height={30}
                        src={property.icon}
                      />
                    </S.PropertyIcon>
                    <S.PropertyTitle>
                      {property.title.split("\n").map((part, i, arr) => (
                        <Fragment key={i}>
                          {part}
                          {i < arr.length - 1 && <br />}
                        </Fragment>
                      ))}
                    </S.PropertyTitle>
                    {property.tooltip && (
                      <S.PropertyTooltip>
                        <S.TooltipStyles maxWidth={property.tooltipWidth} />
                        <Tooltip
                          tooltipClassName={TOOLTIP_CLASS_NAME}
                          description={
                            <S.PropertyTooltipContent
                              dangerouslySetInnerHTML={{
                                __html: property.tooltip,
                              }}
                            />
                          }
                          portal
                          motif="dark"
                          isInteractive
                          position="right"
                          width={property.tooltipWidth}
                        >
                          <S.PropertyTooltipHoverArea />
                          <S.TooltipIcon
                            width={20}
                            height={20}
                            src={tooltipIcon}
                          />
                        </Tooltip>
                      </S.PropertyTooltip>
                    )}
                  </S.PropertyTitleGridItemContent>
                )}
              </S.PropertyTitleGridItem>

              {products.map((product, index) => {
                const isLastProduct = index === products.length - 1;
                const productProperty = product.propertyValues?.find(
                  (product) => product.propertyId === property.id,
                );

                return (
                  <PropertyValueGridItem
                    isLastProduct={isLastProduct}
                    key={`${product.id}-${index}`}
                    productProperty={productProperty}
                    property={property}
                    includeProperty={isSmallScreen}
                    index={index}
                  />
                );
              })}
            </Fragment>
          ))}

          {/**
           * Notes
           */}
          {hasNotes && <Notes products={products} />}

          {/**
           * Buy button
           */}
          <S.EmptyPropertyTitleGridItem stickX />
          {products.map((product, index) =>
            renderButton({
              product,
              index,
              getComingSoonButtonText,
              showBuyButton,
              onLinkClick,
            }),
          )}

          {/**
           * Footnotes
           */}
          {hasFootnotes && <FootNotes products={products} />}
        </StickyGridBody>

        <StickyGridOverlay
          style={{ left: `${leftColumnWidth}px` }}
          topOffset={overlayTopOffset}
        >
          {({ isAtMinX }) => (
            <ModelCompareOverflowScrollButton
              isVisible={!isAtMinX}
              color={leftColumnWidth > 0 ? "rgb(23 49 73 / 0.1)" : "white"}
              rotationDegrees={180}
              onClickColumnDiff={-1}
              totalColumns={products.length}
              totalDeadSpace={leftColumnWidth}
              isSmallScreen={isSmallScreen}
            />
          )}
        </StickyGridOverlay>

        <StickyGridOverlay style={{ right: 0 }} topOffset={overlayTopOffset}>
          {({ isAtMaxX }) => (
            <ModelCompareOverflowScrollButton
              isLeftButton={false}
              isVisible={!isAtMaxX}
              color="white"
              rotationDegrees={0}
              onClickColumnDiff={1}
              totalColumns={products.length}
              totalDeadSpace={leftColumnWidth}
              isSmallScreen={isSmallScreen}
            />
          )}
        </StickyGridOverlay>
      </StickyGrid>
    </section>
  );
};
